/**
 * Value Metrics Card Component
 * 
 * Displays Customer Value, Business Value, and Stakeholder Value metrics
 * with rankings, ratios, and recommended focus area.
 */

import React, { useState, useEffect } from 'react';
import { Card, Progress, Tag, Space, Spin, Alert, Typography, Divider } from 'antd';
import { 
  UserOutlined,
  DollarOutlined,
  TeamOutlined,
  RiseOutlined,
  FallOutlined
} from '@ant-design/icons';
import { CardSectionProps } from '../../../types/compositeCards';
import { apiClient } from '../../../services/apiClient';
import styles from './ValueMetricsCard.module.css';

const { Text, Title } = Typography;

interface ValueMetric {
  score: number;
  ranking: number;
  ratio: number;
  sub_metrics: {
    [key: string]: number;
  };
  trend: 'upward' | 'stable' | 'downward';
}

interface ValueMetricsData {
  customer_value: ValueMetric;
  business_value: ValueMetric;
  stakeholder_value: ValueMetric;
  recommended_focus: 'customer_value' | 'business_value' | 'stakeholder_value';
  systemic_index: number;
}

export const ValueMetricsCard: React.FC<CardSectionProps> = ({ 
  card,
  onAction
}) => {
  const { data } = card;
  const [metrics, setMetrics] = useState<ValueMetricsData | null>(
    'metrics' in data ? (data.metrics as ValueMetricsData) : null
  );
  const [loading, setLoading] = useState(!metrics);
  const [error, setError] = useState<string | null>(null);

  // Get company_id or artifact_tree_id from card data
  // ISSUE-018: Backend now accepts company_id in path parameter
  const companyId = 'companyId' in data ? data.companyId : ('businessModelId' in data ? data.businessModelId : null); // Fallback for backwards compatibility
  const artifactTreeId = 'artifactTreeId' in data ? data.artifactTreeId : null;

  useEffect(() => {
    if (!metrics && (companyId || artifactTreeId)) {
      void loadMetrics();
    }
  }, [companyId, artifactTreeId]);

  const loadMetrics = async () => {
    if (!companyId && !artifactTreeId) {
      setError('Company ID or artifact tree ID required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (artifactTreeId && typeof artifactTreeId === 'string') {
        params.append('artifact_tree_id', artifactTreeId);
      }
      
      const url = companyId 
        ? `/company-models/${companyId}/value-metrics${params.toString() ? '?' + params.toString() : ''}`
        : null;
      
      if (!url) {
        throw new Error('Company ID is required');
      }
      
      const response = await apiClient.get(url);
      setMetrics(response.data);
    } catch (err: any) {
      console.error('Failed to load value metrics:', err);
      setError(err.response?.data?.detail || 'Failed to load value metrics');
    } finally {
      setLoading(false);
    }
  };

  const getMetricIcon = (type: string) => {
    switch (type) {
      case 'customer_value':
        return <UserOutlined />;
      case 'business_value':
        return <DollarOutlined />;
      case 'stakeholder_value':
        return <TeamOutlined />;
      default:
        return null;
    }
  };

  const getMetricColor = (type: string, isRecommended: boolean) => {
    if (isRecommended) {
      return '#ff4d4f'; // Red for recommended focus
    }
    switch (type) {
      case 'customer_value':
        return '#3b82f6'; // Blue
      case 'business_value':
        return '#10b981'; // Green
      case 'stakeholder_value':
        return '#8b5cf6'; // Purple
      default:
        return '#666';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'upward':
        return <RiseOutlined style={{ color: '#10b981' }} />;
      case 'downward':
        return <FallOutlined style={{ color: '#ef4444' }} />;
      default:
        return null;
    }
  };

  const renderMetric = (
    type: 'customer_value' | 'business_value' | 'stakeholder_value',
    metric: ValueMetric,
    label: string
  ) => {
    const isRecommended = metrics?.recommended_focus === type;
    const color = getMetricColor(type, isRecommended);
    const icon = getMetricIcon(type);

    return (
      <Card
        key={type}
        className={`${styles.valueMetricCard} ${isRecommended ? styles.recommendedFocus : ''}`}
        style={{
          border: isRecommended ? `2px solid ${color}` : '1px solid #e8e8e8',
          marginBottom: 16
        }}
      >
        <div className={styles.valueMetricHeader}>
          <Space>
            {icon}
            <Title level={5} style={{ margin: 0 }}>
              {label}
            </Title>
            {isRecommended && (
              <Tag color="red">Recommended Focus</Tag>
            )}
            {getTrendIcon(metric.trend)}
          </Space>
        </div>

        <div className={styles.valueMetricContent}>
          <div className={styles.valueMetricScore}>
            <Progress
              type="circle"
              percent={metric.score}
              format={(percent) => `${percent?.toFixed(1)}`}
              strokeColor={color}
              size={80}
            />
            <div className={styles.valueMetricDetails}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ranking: {metric.ranking.toFixed(1)}/100
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ratio: {metric.ratio.toFixed(3)}
              </Text>
            </div>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          <div className={styles.valueMetricSubMetrics}>
            <Text strong style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
              Sub-metrics:
            </Text>
            {Object.entries(metric.sub_metrics).map(([key, value]) => (
              <div key={key} className={styles.subMetricItem}>
                <Text style={{ fontSize: 11 }}>{key.replace(/_/g, ' ')}:</Text>
                <Text strong style={{ fontSize: 11, marginLeft: 8 }}>
                  {value.toFixed(1)}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading value metrics...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Value Metrics"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  if (!metrics) {
    return (
      <Alert
        message="No Value Metrics Available"
        description="Value metrics could not be calculated for this company model."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div className={styles.valueMetricsCard}>
      <div className={styles.valueMetricsHeader}>
        <Title level={4} style={{ margin: 0 }}>
          Company Value Metrics
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Systemic Index: {metrics.systemic_index.toFixed(1)}/100
        </Text>
      </div>

      <div className={styles.valueMetricsContent}>
        {renderMetric('customer_value', metrics.customer_value, 'Customer Value')}
        {renderMetric('business_value', metrics.business_value, 'Business Value')}
        {renderMetric('stakeholder_value', metrics.stakeholder_value, 'Stakeholder Value')}
      </div>

      {metrics.recommended_focus && (
        <div className={styles.valueMetricsRecommendation}>
          <Alert
            message="Recommended Focus"
            description={`Based on your current metrics, focusing on improving ${metrics.recommended_focus.replace('_', ' ')} is recommended to drive the greatest impact.`}
            type="info"
            showIcon
          />
        </div>
      )}
    </div>
  );
};

